"use client";

import { ThemeProvider } from "@emotion/react";
import {
  Box,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  createTheme,
  Typography,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setAgencyArray,
  setBranchesArray,
  setSelectedBranchesString,
} from "../../../redux/actions/branchActions";
import { filterFunc } from "../../../redux/actions/actions";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

import paperBackFirstImg from "../../../assets/images/masks/paper-back-1.svg";
import paperBackSecondImg from "../../../assets/images/masks/paper-back-2.svg";
import { useTranslation } from "react-i18next";

export default function Branches({ open }) {
  const lightMode = useSelector((state) => state.mode.lightMode);
  const dispatch = useDispatch();

  const { t } = useTranslation();

  const branchsTheme = createTheme({
    palette: {
      mode: lightMode ? "light" : "dark",
    },
    components: {
      MuiSelect: {
        styleOverrides: {
          icon: {
            display: "none", // Hide default arrow
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          root: {
            zIndex: 999999,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            minWidth: "280px",
            borderRadius: "1rem",
            boxShadow:
              "0 0 2px 0 rgba(145 158 171 / 0.2),0 12px 24px -4px rgba(145 158 171 / 0.12)",
            backgroundColor: `rgba(${lightMode ? "255 255 255" : "28 37 46"
              } / 0.9)`,
            backgroundImage: `url(${paperBackFirstImg}), url(${paperBackSecondImg})`,
            backgroundSize: "50%, 50%",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right top, left bottom",
            backdropFilter: "blur(20px)",
            transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)",
            outline: 0,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: "0",
            margin: "0",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: lightMode ? "#f8fafc" : "#374151",
            },
            "&.Mui-selected": {
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: lightMode ? "#f8fafc" : "#374151",
              },
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: "4px",
            "&.Mui-checked": {
              color: lightMode ? "#2563eb" : "#60a5fa",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            border: "none",
          },
          root: {
            borderRadius: "12px",
            backgroundColor: lightMode ? "#f1f5f9" : "#374151",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          },
          input: {
            color: lightMode ? "#1f2937" : "#f9fafb",
            padding: 0,
          },
        },
      },
    },
  });

  const profile = useSelector((state) => state.profile.profile);
  const branchesArray = useSelector((state) => state?.branches?.branches);
  const selectedBranchesString = useSelector(
    (state) => state?.branches?.branchesString
  );
  const pageType = useSelector((state) => state.filters.pageType);
  const searchedTerm = useSelector((state) => state.searchTerm.searchTerm);

  const [branches, setBranches] = useState(branchesArray || []);
  const [, setLoading] = useState(false);

  useEffect(() => {
    if (branchesArray?.length) {
      setBranches(branchesArray);
    }
  }, [branchesArray]);

  const getBranches = async () => {
    setLoading(true);
    const accessToken = localStorage.getItem("accessToken");
    const token = localStorage.getItem("loginUser");

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}auth/branches`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            XRefreshToken: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setBranches(response.data.results);
      if (response.data.results?.length) {
        dispatch(setBranchesArray(response.data.results));
        dispatch(setAgencyArray(response.data.results));
      } else {
        dispatch(setBranchesArray(profile?.agent_id));
        dispatch(setAgencyArray(profile?.agent_id));
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("API isteği sırasında hata oluştu:", error);
    }
  };

  useEffect(() => {
    if (profile) {
      getBranches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const [branchIds, setBranchIds] = useState(selectedBranchesString || []);
  const selectedFilters = useSelector((state) => state.filters.selectedFilters);

  useEffect(() => {
    let newBranchIds = [];
    if (profile) {
      if (!branchIds?.includes(profile?.agent_id)) {
        newBranchIds = [...branchIds];
      } else {
        newBranchIds = branchIds;
      }
    }
    dispatch(setSelectedBranchesString(newBranchIds));
  }, [branchIds, profile, dispatch]);

  useEffect(() => {
    if (Object.keys(selectedFilters).length) {
      dispatch(
        filterFunc(
          selectedFilters,
          pageType,
          searchedTerm,
          selectedBranchesString
        )
      );
    }
  }, [selectedBranchesString, selectedFilters, pageType, searchedTerm]);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    setBranchIds(typeof value === "string" ? value.split(",") : value);
  };

  const getSelectedBranch = () => {
    if (selectedBranchesString?.length === 1) {
      return branches.find(
        (branch) => branch.agent_id === selectedBranchesString[0]
      );
    }
    return null;
  };

  const selectedBranch = getSelectedBranch();

  return profile?.account_type === "main_branch" ? (
    <ThemeProvider theme={branchsTheme}>
      <Box
        sx={{
          // padding: "1rem",
          position: "relative",
          borderRadius: "1rem",
          ml: { xs: 0, lg: 2 },
          // background: light
        }}
      >
        {branches?.length ? (
          <FormControl
            fullWidth
            sx={{
              opacity: open ? "1" : "1",
            }}
          >
            <Select
              multiple
              sx={{
                background: "transparent",
              }}
              value={selectedBranchesString || []}
              onChange={handleChange}
              input={<OutlinedInput />}
              displayEmpty
              renderValue={(selected) => {
                // No selection state
                if (!selected || selected.length === 0) {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {/* <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: lightMode ? "#e5e7eb" : "#6b7280",
                            marginRight: "12px",
                            fontSize: "14px",
                            fontWeight: 600,
                          }}
                        >
                          <TuneIcon
                            sx={{
                              fontSize: "16px",
                              color: lightMode ? "#6b7280" : "#d1d5db",
                            }}
                          />
                        </Avatar> */}
                        <Typography
                          sx={{
                            color: lightMode ? "#6b7280" : "#9ca3af",
                            fontSize: { xs: "12px", md: "16px" },
                            fontWeight: 500,
                          }}
                        >
                          {t("branches")}
                        </Typography>
                      </Box>
                      <UnfoldMoreIcon
                        sx={{
                          color: lightMode ? "#6b7280" : "#9ca3af",
                          fontSize: "20px",
                        }}
                      />
                    </Box>
                  );
                }

                // Single selection
                if (selected.length === 1 && selectedBranch) {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {/* <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: getBranchIcon(index),
                            marginRight: "12px",
                            fontSize: "14px",
                            fontWeight: 600,
                          }}
                        >
                          {selectedBranch.agent_name?.charAt(0)?.toUpperCase()}
                        </Avatar> */}
                        <Typography
                          sx={{
                            color: lightMode ? "#1f2937" : "#f9fafb",
                            fontSize: "16px",
                            fontWeight: 500,
                            display: { xs: "block", md: "block" },
                          }}
                        >
                          {selectedBranch.agent_name}
                        </Typography>
                      </Box>
                      <UnfoldMoreIcon
                        sx={{
                          color: lightMode ? "#6b7280" : "#9ca3af",
                          fontSize: "20px",
                        }}
                      />
                    </Box>
                  );
                }

                // Multiple selection
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {/* <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: "#6366f1",
                          marginRight: "12px",
                          fontSize: "12px",
                          fontWeight: 600,
                        }}
                      >
                        {selected.length}
                      </Avatar> */}
                      <Typography
                        sx={{
                          color: lightMode ? "#1f2937" : "#f9fafb",
                          fontSize: "16px",
                          fontWeight: 500,
                        }}
                      >
                        {`${selected.length} branches selected`}
                      </Typography>
                    </Box>
                    <UnfoldMoreIcon
                      sx={{
                        color: lightMode ? "#6b7280" : "#9ca3af",
                        fontSize: "20px",
                      }}
                    />
                  </Box>
                );
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 400,
                    width: 320,
                  },
                },
              }}
            >
              {branches?.map((branch, index) => {
                const isSelected = selectedBranchesString.includes(
                  branch.agent_id
                );

                return (
                  <MenuItem
                    key={branch.agent_id}
                    value={branch.agent_id}
                    sx={{
                      borderBottom:
                        branches?.length !== index + 1
                          ? `1px dashed ${lightMode ? "#e9ecee" : "#333e47"}`
                          : "unset",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        padding: "12px 16px",
                        borderRadius: "8px",
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        sx={{
                          marginRight: "8px",
                          padding: "4px",
                        }}
                      />

                      {/* <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: getBranchIcon(index),
                          marginRight: "12px",
                          fontSize: "16px",
                          fontWeight: 600,
                        }}
                      >
                        {branch.agent_name?.charAt(0)?.toUpperCase()}
                      </Avatar> */}

                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          color: lightMode ? "#1f2937" : "#f9fafb",
                          fontWeight: 500,
                        }}
                      >
                        {branch.agent_name}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        ) : null}
      </Box>
    </ThemeProvider>
  ) : null;
}
